import axios, { AxiosResponse } from "axios";
import {
  AccurateModuleDefinition,
  AccuratePostingSubmitPayload,
  AccuratePostingTransaction,
} from "../types/accuratePosting";
import { PriceCategory, Product, SerialNumberItem } from "../types/product";
import { ReceiveTypes } from "../types/receive";

// Base URLs
const BASE_URL = process.env.REACT_APP_BASE_URL;

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    Accept: "application/json, text/plain, */*",
  },
});

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("token") || "";
  const session = localStorage.getItem("session") || "";
  const accessToken = localStorage.getItem("accessToken") || "";
  const baseUrl = localStorage.getItem("baseUrl") || "";

  return {
    Authorization: `Bearer ${token}`,
    Token: accessToken,
    Session: session,
    baseUrl: `${baseUrl}/`,
  };
};
interface TokenResponse {
  access_token: string;
  refresh_token: string;
  session_id: string;
  business_id: string;
  base_url: string;
  username: string;
}
interface AuthResponse {
  token: string;
  expiresIn: number;
  success: boolean;
  data: {
    token: TokenResponse[];
  };
}
enum STATUS_RESPONSE {
  SUCCESS = "success",
  ERROR = "error",
}
interface GetDatabaseResponse {
  data: {
    token: TokenResponse[];
    success: boolean;
    errorMessage: string;
    status: STATUS_RESPONSE;
  };
}
interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  message?: string;
}

export const fetchProductDetails = async (
  query: string,
  priceCategoryName?: string,
): Promise<{ data: { items: Product[] } }> => {
  try {
    const response: AxiosResponse<{ data: { items: Product[] } }> =
      await api.get("/api/barcode/v1/item", {
        params: {
          keyword: query,
          ...(priceCategoryName ? { priceCategoryName } : {}),
        },
        headers: getAuthHeaders(),
      });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || "Failed to fetch product");
  }
};

export const fetchPriceCategories = async (): Promise<{
  data: PriceCategory[];
}> => {
  try {
    const databaseId = localStorage.getItem("business_id") || "";
    if (!databaseId) {
      throw new Error("Accurate database is not selected");
    }

    const response: AxiosResponse<{ data: PriceCategory[] }> = await api.get(
      "/api/price-category",
      {
        params: { databaseId },
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        error.message ||
        "Failed to fetch price categories",
    );
  }
};

export const getSerialNumbers = async (
  itemNo: string,
  keyword = "",
): Promise<{ data: SerialNumberItem[] }> => {
  try {
    const response = await api.get<{
      status: string;
      data: SerialNumberItem[];
    }>(`/api/barcode/v1/item/${encodeURIComponent(itemNo)}/serial-numbers`, {
      params: { limit: 50, keyword },
      headers: getAuthHeaders(),
    });

    if (response.data.status !== "success" || !Array.isArray(response.data.data)) {
      throw new Error("Invalid serial number response");
    }

    return { data: response.data.data };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        error.message ||
        "Failed to fetch serial numbers",
    );
  }
};

export const fetchReceives = async (
  query: string
): Promise<{ data: ReceiveTypes[] }> => {
  try {
    const response: AxiosResponse<{ data: ReceiveTypes[] }> = await api.get(
      "/api/barcode/v1/item/receive",
      {
        params: { transNumber: query },
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || "Failed to fetch receive");
  }
};

export const postItems = async (
  data: Array<Product>
): Promise<{ data: Product[] }> => {
  try {
    const response: AxiosResponse<{ data: Product[] }> = await api.post(
      "/api/barcode/v1/item",
      data,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || "Failed to post item");
  }
};

export const authorize = async (token: string , username:string): Promise<AuthResponse> => {
  try {    
    const response: AxiosResponse<AuthResponse> = await api.get(
      `/api/mit-client/get-accurate/credentials`,
      {
        params: { mitToken: token , username:username },
        headers: {
          Accept: "application/json, text/plain, */*",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || "Authorization failed");
  }
};

export const login = async (
  email: string,
  password: string
): Promise<LoginResponse> => {
  try {
    const response: AxiosResponse<LoginResponse> = await api.post(
      `/api/mit-client/auth/login`,
      { email, password },
      {
        headers: {
          Authorization: "Bearer null",
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || "Failed to login");
  }
};

export const generateBarcode = async (keyword: string): Promise<string> => {
  try {
    const response: AxiosResponse<string> = await api.get(
      "/api/mit-client/generate-barcode-item",
      {
        params: { keyword },
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Failed to generate barcode"
    );
  }
};

export const savePrintSettings = async (data: any): Promise<any> => {
  try {
    const bussinessId = localStorage.getItem("business_id") || "";
    const response: AxiosResponse<any> = await api.post(
      "/api/mit-client/print-setting?databaseId=" + bussinessId,
      {
        payloadPrintSetting: data,
      },
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Failed to save print settings"
    );
  }
};

export const getPrintSettings = async (): Promise<any> => {
  try {
    const bussinessId = localStorage.getItem("business_id") || "";
    const response: AxiosResponse<any> = await api.get(
      "/api/mit-client/print-setting?databaseId=" + bussinessId,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Failed to get print settings"
    );
  }
};

export const saveBarcode = async (data: any): Promise<any> => {
  try {
    const bussinessId = localStorage.getItem("business_id") || "";
    const response: AxiosResponse<any> = await api.post(
      "/api/mit-client/save-barcode-item?databaseId=" + bussinessId,
      data,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || "Failed to save barcode");
  }
};

export const openDatabase = async (
  database: string,
  token: string
): Promise<GetDatabaseResponse> => {
  try {
    const response: AxiosResponse<GetDatabaseResponse> = await api.get(
      `/api/mit-client/get-accurate/credentials-by-database-id`,
      {
        params: { databaseId: database },
        headers: {
          Accept: "application/json, text/plain, */*",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || "Authorization failed");
  }
};

export const getAccurateModules = async (): Promise<{
  data: AccurateModuleDefinition[];
}> => {
  try {
    const response: AxiosResponse<{ data: AccurateModuleDefinition[] }> =
      await api.get("/api/modules", {
        headers: getAuthHeaders(),
      });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || "Failed to fetch modules");
  }
};

export const getAccurateTransaction = async (
  moduleType: string,
  number: string
): Promise<{ data: AccuratePostingTransaction }> => {
  try {
    const databaseId = localStorage.getItem("business_id") || "";
    if (!databaseId) {
      throw new Error("Accurate database is not selected");
    }
    const response: AxiosResponse<
      | { data: AccuratePostingTransaction }
      | { s: boolean; d: AccuratePostingTransaction }
    > =
      await api.get("/api/accurate/transactions", {
        params: { databaseId, moduleType, number },
        headers: getAuthHeaders(),
      });
    return "d" in response.data ? { data: response.data.d } : response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch accurate transaction"
    );
  }
};

export interface SerialNumberMutation {
  serialNumber: string;
  quantity: number;
}

interface SerialNumberMutationResponseItem {
  serialNumber?: {
    number?: string;
  };
  transaction?: {
    quantity?: number;
  };
}

const formatMutationDate = (value?: string): string | undefined => {
  if (!value) return undefined;
  const isoDate = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return isoDate ? `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}` : value;
};

export const getSerialNumberMutations = async (
  itemNo: string,
  fromDate?: string,
  toDate?: string,
): Promise<{ data: SerialNumberMutation[] }> => {
  try {
    const databaseId = localStorage.getItem("business_id") || "";
    if (!databaseId) {
      throw new Error("Accurate database is not selected");
    }
    const response = await api.get<{
      status: string;
      data: SerialNumberMutationResponseItem[];
    }>("/api/serial-number-mutation", {
      params: {
        databaseId,
        itemNo,
        ...(fromDate ? { fromDate: formatMutationDate(fromDate) } : {}),
        ...(toDate ? { toDate: formatMutationDate(toDate) } : {}),
      },
      headers: getAuthHeaders(),
    });

    if (response.data.status !== "success" || !Array.isArray(response.data.data)) {
      throw new Error("Invalid serial number mutation response");
    }

    return {
      data: response.data.data.flatMap((mutation) => {
        const serialNumber = mutation.serialNumber?.number;
        const quantity = mutation.transaction?.quantity;
        return serialNumber && typeof quantity === "number"
          ? [{ serialNumber, quantity }]
          : [];
      }),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        error.message ||
        "Failed to fetch serial number mutations",
    );
  }
};

export const submitAccurateTransaction = async (
  payload: AccuratePostingSubmitPayload
): Promise<{ data: AccuratePostingTransaction }> => {
  try {
    const databaseId = localStorage.getItem("business_id") || "";
    if (!databaseId) {
      throw new Error("Accurate database is not selected");
    }
    const response: AxiosResponse<{ data: AccuratePostingTransaction }> =
      await api.put("/api/accurate/transactions", payload, {
        params: { databaseId },
        headers: getAuthHeaders(),
      });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Failed to submit accurate transaction"
    );
  }
};
